import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { isImage, isVideo } from './utils';

const Frame = styled('div', { shouldForwardProp: p => p !== 'aspectRatio' && p !== 'dark' })(
    ({ aspectRatio = '1 / 1', dark }) => ({
        position: 'relative',
        width: '100%',
        aspectRatio,
        backgroundColor: dark ? '#000' : '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    }),
);

const Image = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
});

const Video = styled('video')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    backgroundColor: '#000',
});

const NavButton = styled(IconButton, { shouldForwardProp: p => p !== 'side' })(({ side }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    color: '#111',
    width: 28,
    height: 28,
    '& svg': { fontSize: 18 },
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-disabled': { backgroundColor: 'rgba(255, 255, 255, 0.4)', color: '#777' },
}));

const Dots = styled('div')({
    position: 'absolute',
    bottom: 8,
    left: 0,
    right: 0,
    display: 'flex',
    gap: 4,
    justifyContent: 'center',
});

const Dot = styled('span', { shouldForwardProp: p => p !== 'active' })(({ active }) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: active ? '#fff' : 'rgba(255, 255, 255, 0.5)',
}));

const Counter = styled('span')({
    position: 'absolute',
    top: 8,
    right: 8,
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
});

const EmptyOverlay = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: 'rgba(0, 0, 0, 0.45)',
    textAlign: 'center',
    padding: 16,
});

const MediaSlide = ({ media = [], aspectRatio = '1 / 1', allowVideo = true, emptyLabel, dark = false }) => {
    const { t } = useTranslation();
    // Drop unknown-type items so the carousel never points at a blank frame.
    const items = useMemo(
        () => (Array.isArray(media) ? media : []).filter(m => m?.link && (isImage(m) || (allowVideo && isVideo(m)))),
        [media, allowVideo],
    );
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (index > items.length - 1) setIndex(Math.max(0, items.length - 1));
    }, [items.length, index]);

    if (items.length === 0) {
        return (
            <Frame aspectRatio={aspectRatio} dark={dark}>
                <EmptyOverlay>
                    <ImageOutlinedIcon sx={{ fontSize: 36, opacity: 0.5 }} />
                    <Typography variant="caption">{emptyLabel || t('SocialPosts.noMediaPreview')}</Typography>
                </EmptyOverlay>
            </Frame>
        );
    }

    const current = items[Math.min(index, items.length - 1)];
    const showNav = items.length > 1;

    return (
        <Frame aspectRatio={aspectRatio} dark={dark}>
            {isVideo(current) ? (
                // muted is required for autoplay; matches how FB/IG/TikTok play feed videos.
                <Video
                    key={current.link}
                    src={current.link}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    preload="metadata"
                />
            ) : isImage(current) ? (
                <Image src={current.link} alt="" />
            ) : null}

            {showNav && (
                <>
                    <NavButton
                        side="left"
                        size="small"
                        onClick={() => setIndex(i => Math.max(0, i - 1))}
                        disabled={index === 0}
                        aria-label="Previous"
                    >
                        <ChevronLeftIcon />
                    </NavButton>
                    <NavButton
                        side="right"
                        size="small"
                        onClick={() => setIndex(i => Math.min(items.length - 1, i + 1))}
                        disabled={index === items.length - 1}
                        aria-label="Next"
                    >
                        <ChevronRightIcon />
                    </NavButton>
                    <Counter>
                        {index + 1} / {items.length}
                    </Counter>
                    <Dots>
                        {items.map((item, i) => (
                            <Dot key={`${item.link}-${i}`} active={i === index} />
                        ))}
                    </Dots>
                </>
            )}
        </Frame>
    );
};

export default MediaSlide;
